import { gql, useQuery } from '@apollo/client';

import Entity from '../../components/entity';

import type { GetCurrentUserQuery, GetCurrentUserQueryVariables } from './__generated__/useCurrentUser.gql';

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      firstName
      lastName
      preferredName
      name
      email
      phoneNumber
      phoneNumberVerifiedAt
      avatarUrl
      timezone
      inferredTimezone
      createdAt
      ...EntityAvatar
    }
  }
  ${Entity.Avatar.Fragment}
`;

export type CurrentUser = GetCurrentUserQuery['getCurrentUser'];

/**
 * Reads the current viewer's `User` directly via the `getCurrentUser` root
 * query, so callers that only need identity fields (name, phone, avatar) don't
 * tunnel through `getCurrentSession`. Session-scoped concerns — the auth gate
 * and impersonation — stay on `useCurrentSession`.
 *
 * @returns `user` (the viewer's `User`, or `null` while loading / logged out)
 *   plus Apollo's `loading`, `error`, and `refetch`.
 */
export const useCurrentUser = () => {
  const { data, loading, error, refetch } = useQuery<GetCurrentUserQuery, GetCurrentUserQueryVariables>(
    GET_CURRENT_USER,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  return { user: data?.getCurrentUser ?? null, loading, error, refetch };
};
